import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Upload, File, Trash2 } from 'lucide-react';
import { documentsApi } from '@/api/endpoints/documents.api';
import type { DocumentType, DocumentTypeInfo } from '@/types/document.types';

interface DocumentUploadDialogProps {
  patientId: string;
  taskId?: string;
  demandId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.doc', '.docx', '.txt'];

export default function DocumentUploadDialog({
  patientId,
  taskId,
  demandId,
  onClose,
  onSuccess,
}: DocumentUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType | ''>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [isConfidential, setIsConfidential] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeInfo[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await documentsApi.getDocumentTypes();
        setDocumentTypes(types);
      } catch (err) {
        console.error('Erro ao carregar tipos de documento:', err);
        // Fallback types
        setDocumentTypes([
          { value: 'LAB_RESULT', label: 'Resultado de Exame', description: 'Resultados de exames laboratoriais' },
          { value: 'PRESCRIPTION', label: 'Prescrição', description: 'Prescrições médicas' },
          { value: 'MEDICAL_REPORT', label: 'Laudo Médico', description: 'Laudos e relatórios médicos' },
          { value: 'IMAGING_REPORT', label: 'Exame de Imagem', description: 'Raio-X, Tomografia, etc.' },
          { value: 'DISCHARGE_SUMMARY', label: 'Sumário de Alta', description: 'Resumo de alta hospitalar' },
          { value: 'REFERRAL', label: 'Encaminhamento', description: 'Guias de encaminhamento' },
          { value: 'CERTIFICATE', label: 'Atestado', description: 'Atestados médicos' },
          { value: 'OTHER', label: 'Outro', description: 'Outros documentos' },
        ]);
      }
    };
    fetchTypes();
  }, []);

  const validateFile = useCallback((f: File): string | null => {
    if (f.size > MAX_FILE_SIZE) {
      return 'Arquivo excede o tamanho máximo de 20MB';
    }
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Tipo de arquivo não permitido. Use: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    return null;
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const f = e.dataTransfer.files[0];
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        return;
      }
      setFile(f);
    }
  }, [validateFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        return;
      }
      setFile(f);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Selecione um arquivo');
      return;
    }
    if (!documentType) {
      setError('Selecione o tipo de documento');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await documentsApi.upload(file, patientId, documentType, {
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        documentDate: documentDate || undefined,
        taskId,
        demandId,
        isConfidential,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error('Erro ao fazer upload:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer upload. Tente novamente.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Enviar Documento</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {/* File Drop Zone */}
          <label
            className={`block cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5'
                : file
                  ? 'border-green-300 bg-green-50'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/30'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              onChange={handleFileChange}
              accept={ALLOWED_EXTENSIONS.join(',')}
              className="hidden"
            />
            {file ? (
              <div className="flex items-center justify-center gap-4">
                <File className="h-10 w-10 text-green-600" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  Arraste o arquivo aqui ou <span className="text-primary hover:underline">clique para selecionar</span>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  PDF, imagens ou documentos até 20MB
                </p>
              </>
            )}
          </label>

          {/* Document Type */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Tipo de Documento <span className="text-red-500">*</span>
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as DocumentType)}
              className="w-full rounded-lg border bg-background p-3"
            >
              <option value="">Selecione o tipo</option>
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="mb-1 block text-sm font-medium">Título (opcional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Hemograma completo - Janeiro 2024"
              className="w-full rounded-lg border bg-background p-3"
              maxLength={255}
            />
          </div>

          {/* Document Date */}
          <div>
            <label className="mb-1 block text-sm font-medium">Data do Documento (opcional)</label>
            <input
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
              className="w-full rounded-lg border bg-background p-3"
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium">Observações (opcional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Informações adicionais sobre o documento..."
              rows={2}
              className="w-full rounded-lg border bg-background p-3"
            />
          </div>

          {/* Confidential */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isConfidential"
              checked={isConfidential}
              onChange={(e) => setIsConfidential(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="isConfidential" className="text-sm">
              Documento confidencial (não visível no portal do paciente)
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border px-4 py-2 hover:bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !file || !documentType}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Enviar Documento
          </button>
        </div>
      </div>
    </div>
  );
}
